import { lightTheme } from '@/customTheme'
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { styled, StyleObject } from 'styletron-react'
import ArrowIcon from './FloatingIcons/ArrowIcon'
import TightArrowIcon from './FloatingIcons/TightArrowIcon'
import Icons from '../Icons'
import { FloatingInspectorButtonWidth, WidthType } from './types'
import './FloatingInspectorButton.module.css'
import FloatingTooltip from './SharedComponents/Tooltip/FloatingTooltip'
import uniqueId from '@/utils/unique'
import WithPopover, { WithPopoverHandle } from './WithPopover'

export interface FloatingInspectorButtonHandle {
  closePopover: () => void
}

export type FloatingInspectorButtonArrow = 'up' | 'down' | 'none'
export type PopoverContentType = (buttonRef: React.RefObject<HTMLElement>, closePopover?: () => void) => React.ReactNode

export interface FloatingInspectorButtonProps {
  showArrow?: FloatingInspectorButtonArrow
  showBorder?: boolean
  widthType?: WidthType
  isGroupButton?: boolean
  isSecondaryStyle?: boolean
  onClick: React.MouseEventHandler<HTMLButtonElement>
  selected?: boolean
  disabled?: boolean
  nonInteractive?: boolean
  disabledStyle?: 'grayBackground' | 'transparentBackground'
  style?: StyleObject & React.CSSProperties
  className?: string
  children?: React.ReactNode
  placementTooltip?: 'top' | 'bottom' | 'left' | 'right'
  marginTooltip?: number
  tooltipText?: string
  tooltipOptions?: {
    design?: 'default' | 'compact'
    maxWidth?: number | string
    textAlign?: 'left' | 'center' | 'right'
    ignoreBoundary?: boolean
    zIndex?: number
  }
  popoverOnHover?: (buttonRef: React.RefObject<HTMLButtonElement>) => React.ReactNode
  popoverContent?: PopoverContentType
  onPopoverOpenChange?: (isOpen: boolean) => void
  id?: string
  /** Accessible name, for an icon-only button whose tooltip is not its label. */
  ariaLabel?: string
  disableHover?: boolean
  // long press API
  onLongPressStart?: (e: React.SyntheticEvent<HTMLButtonElement>) => void
  onLongPressEnd?: (e: React.SyntheticEvent<HTMLButtonElement>) => void
  onLongPressInterval?: (e: React.SyntheticEvent<HTMLButtonElement>) => void
  longPressDelay?: number
  longPressInterval?: number
  borderRadiusStyle?: 'rectangle' | 'rounded'
  arrowVariant?: 'default' | 'tight'
}

const Container = styled<
  'button',
  {
    $widthType: WidthType
    $disabled: boolean
    $selected?: boolean
    $showBorder?: boolean
    $isGroupButton?: boolean
    $isSecondaryStyle?: boolean
    $buttonStyle?: 'rectangle' | 'rounded'
    $nonInteractive?: boolean
    $style?: StyleObject & React.CSSProperties
  }
>('button', props => ({
  position: 'relative',
  height: '32px',
  minWidth: '32px',
  padding: props.$widthType ? '4px 8px' : '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: props.$widthType === 'FixedTextTwoSections' ? 'space-between' : 'start',
  gap: props.$isGroupButton  ? '4px' : '6px',
  border: props.$showBorder ? `1px solid ${lightTheme.colors.grayScale100}` : 'none',
  borderRadius: props.$buttonStyle === 'rounded' ? '100px' : '8px',
  background: props.$disabled ? lightTheme.colors.white : props.$selected ? lightTheme.colors.grayScale100 : lightTheme.colors.white,
  color: props.$disabled ? lightTheme.colors.text.text_200_disable : lightTheme.colors.blackGray,
  cursor: 'pointer',
  pointerEvents: props.$disabled || props.$nonInteractive ? 'none' : 'auto',
  marginRight: props.$widthType === 'FixedTextTwoSections' ? '8px' : '0px',

  ...(props.$widthType && { width: `${FloatingInspectorButtonWidth[props.$widthType]}px` }),

  // second style
  ...(props.$isSecondaryStyle && {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
    borderRadius: '4px',
  }),

  // Interactions
  ':hover':
    props.$disabled || props.$isGroupButton || props.$selected || props.$nonInteractive
      ? {}
      : {
          background: lightTheme.colors.grayScale50,
        },
  ':active':
    props.$disabled || props.$isGroupButton || props.$nonInteractive
      ? {}
      : {
          background: lightTheme.colors.grayScale100,
        },
  ...props.$style
}))

export const IconWrapper = styled('span', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '24px',
  height: '24px',
})

export const MainText = styled('span', (props: { $disabled?: boolean; $fontWeight?: number; $truncate?: boolean }) => ({
  minWidth: '24px',
  width: 'fit-content',
  flexShrink: 1,
  ...lightTheme.typography.Small12medium,
  color: props.$disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray,
  ...(props.$fontWeight && { fontWeight: props.$fontWeight }),
  ...(props.$truncate !== false && {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
}))

export const SecondText = styled('span', (props: { $disabled?: boolean }) => ({
  whiteSpace: 'nowrap',
  color: props.$disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray,
  fontSize: '10px',
  fontWeight: 500,
  lineHeight: '24px',
}))

export const FloatingInspectorButton = forwardRef<FloatingInspectorButtonHandle, FloatingInspectorButtonProps>((props, ref) => {
  const {
    showArrow = 'none',
    showBorder = false,
    widthType,
    isGroupButton,
    isSecondaryStyle = false,
    onClick,
    selected,
    disabled,
    nonInteractive,
    disabledStyle = 'grayBackground',
    style,
    className,
    tooltipText,
    placementTooltip = 'top',
    marginTooltip = 2,
    tooltipOptions,
    popoverOnHover,
    popoverContent,
    onPopoverOpenChange,
    children,
    id,
    ariaLabel,
    disableHover = false,
    onLongPressStart,
    onLongPressEnd,
    onLongPressInterval,
    longPressDelay = 300,
    longPressInterval = 80,
    borderRadiusStyle: buttonStyle,
    arrowVariant = 'default',
  } = props

  const uid = useRef(uniqueId());
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<WithPopoverHandle>(null)

  const [isHover, setIsHover] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const hoverTimeoutId = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdTimeoutRef = useRef<number | null>(null)
  const repeatIntervalRef = useRef<number | null>(null)
  const longPressActiveRef = useRef(false)
  const suppressNextClickRef = useRef(false)

  // Expose closePopover by delegating to WithPopover
  useImperativeHandle(ref, () => ({
    closePopover: () => {
      popoverRef.current?.closePopover()
      setIsHover(false)
    }
  }), [])

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current)
      if (repeatIntervalRef.current) clearInterval(repeatIntervalRef.current)
      if (hoverTimeoutId.current) clearTimeout(hoverTimeoutId.current)
    }
  }, [])

  const startLongPress = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (!onLongPressStart && !onLongPressInterval && !onLongPressEnd) return
    if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current)
    if (repeatIntervalRef.current) clearInterval(repeatIntervalRef.current)
    longPressActiveRef.current = false
    holdTimeoutRef.current = window.setTimeout(() => {
      longPressActiveRef.current = true
      onLongPressStart && onLongPressStart(e)
      if (onLongPressInterval) {
        repeatIntervalRef.current = window.setInterval(() => {
          onLongPressInterval(e)
        }, longPressInterval)
      }
      // Default behavior: suppress click after long-press to avoid double actions
      suppressNextClickRef.current = true
    }, longPressDelay)
  }

  const endLongPress = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current)
      holdTimeoutRef.current = null
    }
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current)
      repeatIntervalRef.current = null
    }
    if (longPressActiveRef.current) {
      onLongPressEnd && onLongPressEnd(e)
    }
    longPressActiveRef.current = false
  }

  const handleMouseLeave: React.MouseEventHandler<HTMLButtonElement> = e => {
    setIsHover(false)
    if (hoverTimeoutId.current) {
      clearTimeout(hoverTimeoutId.current)
    }
  }

  const resolvedTooltipDesign = tooltipOptions?.design
  const resolvedTooltipMaxWidth = tooltipOptions?.maxWidth
  const resolvedTooltipTextAlign = tooltipOptions?.textAlign
  const resolvedTooltipIgnoreBoundary = tooltipOptions?.ignoreBoundary
  const resolvedTooltipZIndex = tooltipOptions?.zIndex
  const hasArrow = showArrow !== 'none'
  const arrowRotation = showArrow === 'up' ? 'rotate(180deg)' : undefined
  const isSelected = isPopoverOpen || !!selected

  const button = (
    <Container
      ref={buttonRef}
      id={id}
      aria-label={ariaLabel}
      data-selected={isSelected ? 'true' : 'false'}
      type="button"
      disabled={disabled}
      className={className}
      onClick={(e) => {
        if (nonInteractive) return
        if (suppressNextClickRef.current) {
          e.preventDefault()
          e.stopPropagation()
          suppressNextClickRef.current = false
          return
        }
        onClick(e)
        // Callers may handle an access gate instead of opening settings.
        if (popoverContent && !e.defaultPrevented) {
          popoverRef.current?.togglePopover()
        }
      }}
      onMouseDown={(e) => { startLongPress(e) }}
      onMouseUp={(e) => { endLongPress(e) }}
      onTouchStart={(e) => { startLongPress(e) }}
      onTouchEnd={(e) => { endLongPress(e) }}
      onTouchCancel={(e) => { endLongPress(e) }}
      $widthType={widthType}
      $disabled={!!disabled}
      $selected={isSelected}
      $showBorder={showBorder}
      $isGroupButton={isGroupButton}
      $isSecondaryStyle={isSecondaryStyle}
      $buttonStyle={buttonStyle}
      $nonInteractive={nonInteractive}
      $style={style}
      style={{
        ...(disabled && hasArrow && {background: disabledStyle === 'grayBackground' ? lightTheme.colors.grayScale100 : lightTheme.colors.white}),
        ...(hasArrow && { padding: style?.padding ?? '4px 8px' }),
      }}
      onMouseEnter={() => {
        hoverTimeoutId.current = setTimeout(() => {
          setIsHover(!disableHover)
        }, popoverOnHover ? 500 : 0)
      }}
      onMouseLeave={(e) => { endLongPress(e); handleMouseLeave(e) }}
    >
      {children}
      {hasArrow && (
        arrowVariant === 'tight' ? (
          <TightArrowIcon
            fill={disabled ? lightTheme.colors.grayScale200 : undefined}
            style={arrowRotation ? { transform: arrowRotation } : undefined}
          />
        ) : (
          <ArrowIcon
            fill={disabled ? lightTheme.colors.grayScale200 : undefined}
            style={arrowRotation ? { transform: arrowRotation } : undefined}
          />
        )
      )}
      {tooltipText && isHover && !isPopoverOpen ? (
        <FloatingTooltip
          tooltipText={tooltipText}
          placement={placementTooltip}
          margin={marginTooltip}
          design={resolvedTooltipDesign}
          maxWidth={resolvedTooltipMaxWidth}
          textAlign={resolvedTooltipTextAlign}
          ignoreBoundary={resolvedTooltipIgnoreBoundary}
          zIndex={resolvedTooltipZIndex}
        />
      ) : null}
      {/* video popover on hover */}
      {isHover && popoverOnHover ? popoverOnHover(buttonRef) : null}
    </Container>
  )

  if (popoverContent) {
    return (
      <WithPopover
        ref={popoverRef}
        popoverContent={popoverContent}
        id={uid.current}
        onOpenChange={(isOpen) => {
          setIsPopoverOpen(isOpen)
          if (!isOpen) setIsHover(false)
          onPopoverOpenChange?.(isOpen)
        }}
      >
        {button}
      </WithPopover>
    )
  }

  return button
})
